import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ApiService } from '../api.service';
import { Observable, catchError, map, of } from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class WeightScaleService {
  private localAgentUrl = 'http://127.0.0.1:9001';

  constructor(
    private apiService: ApiService,
    private http: HttpClient,
    private ngZone: NgZone
  ) {
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && this.isDirectBrowserMode) {
          if (!this.isReadingDirectly || !this.directPort || !this.directPort.readable) {
            console.log('Tab became visible: Web Serial loop was inactive. Auto-reconnecting...');
            this.autoConnectDirectBrowserSerial(
              this.lastBaudRate,
              this.lastDivisor,
              this.lastStartString,
              this.lastEndString
            );
          }
        }
      });
    }
  }

  /**
   * Fetch settings from Laravel Backend
   */
  getSettings(): Observable<any> {
    return this.apiService.get('weight-scale/settings');
  }

  /**
   * Save settings to Laravel Backend
   */
  saveSettings(settings: any): Observable<any> {
    return this.apiService.post('weight-scale/settings', settings);
  }

  /**
   * Initialize local Express agent with credentials
   */
  initializeLocalAgent(ownerId: number): Observable<any> {
    // strip /api/ from base URL to get root backend url
    const rootBackendUrl = environment.apiBaseUrl.replace(/\/api\/?$/, '');
    const token = sessionStorage.getItem('token') || '';

    const payload = {
      backend_url: rootBackendUrl,
      auth_token: token,
      owner_id: ownerId,
      agent_id: `agent_owner_${ownerId}`
    };

    return this.http.post(`${this.localAgentUrl}/init`, payload).pipe(
      catchError(err => {
        console.warn('Local weighing scale agent is not running or unreachable.');
        return of({ success: false, error: err });
      })
    );
  }

  isDirectBrowserMode: boolean = false;
  private directPort: any = null;
  private directReader: any = null;
  private directLatestWeight: number = 0;
  private isReadingDirectly: boolean = false;
  private directDivisor: number = 1;
  private directStartString: string = '';
  private directEndString: string = '';
  private lastBaudRate: number = 9600;
  private lastDivisor: number = 1;
  private lastStartString: string = '';
  private lastEndString: string = '';

  /**
   * Helper to unescape control character representations (e.g. \r to ASCII 13, \u0002 to ASCII 2)
   */
  private unescapeString(str: string): string {
    if (!str) return '';
    try {
      return str
        .replace(/\\r/g, '\r')
        .replace(/\\n/g, '\n')
        .replace(/\\t/g, '\t')
        .replace(/\\u([0-9a-fA-F]{4})/g, (match, grp) => String.fromCharCode(parseInt(grp, 16)));
    } catch (e) {
      console.warn('Failed to unescape custom string:', e);
      return str;
    }
  }

  /**
   * Initialize Direct Browser Web Serial connection
   */
  async requestDirectBrowserSerial(
    baudRate: number = 9600,
    divisor: number = 1,
    startString: string = '',
    endString: string = ''
  ): Promise<boolean> {
    this.lastBaudRate = baudRate;
    this.lastDivisor = divisor;
    this.lastStartString = startString;
    this.lastEndString = endString;

    this.directDivisor = Number(divisor) || 1;
    this.directStartString = this.unescapeString(startString);
    this.directEndString = this.unescapeString(endString);

    if (!('serial' in navigator)) {
      alert('Your browser does not support the Web Serial API. Please use Google Chrome or Microsoft Edge.');
      return false;
    }

    try {
      // Request serial port immediately to preserve user activation gesture
      const selectedPort = await (navigator as any).serial.requestPort();

      // Close any existing connection after requesting the new port
      await this.closeDirectBrowserSerial();

      this.isDirectBrowserMode = true;
      this.directPort = selectedPort;
      await this.directPort.open({ baudRate: Number(baudRate) || 9600 });
      
      this.startReadingDirectly();
      return true;
    } catch (e) {
      console.error('Error connecting to Web Serial Port:', e);
      this.isDirectBrowserMode = false;
      return false;
    }
  }

  /**
   * Auto-connect to previously authorized browser ports
   */
  async autoConnectDirectBrowserSerial(
    baudRate: number = 9600,
    divisor: number = 1,
    startString: string = '',
    endString: string = ''
  ): Promise<boolean> {
    this.lastBaudRate = baudRate;
    this.lastDivisor = divisor;
    this.lastStartString = startString;
    this.lastEndString = endString;

    this.directDivisor = Number(divisor) || 1;
    this.directStartString = this.unescapeString(startString);
    this.directEndString = this.unescapeString(endString);

    if (!('serial' in navigator)) return false;

    if (this.isDirectBrowserMode && this.directPort && this.isReadingDirectly && this.directPort.readable) {
      console.log('Already connected to Web Serial and reading. Bypassing auto-connect.');
      return true;
    }
    
    try {
      const ports = await (navigator as any).serial.getPorts();
      if (ports && ports.length > 0) {
        await this.closeDirectBrowserSerial();
        this.isDirectBrowserMode = true;
        this.directPort = ports[0];
        await this.directPort.open({ baudRate: Number(baudRate) || 9600 });
        this.startReadingDirectly();
        return true;
      }
    } catch (e) {
      console.warn('Failed to auto-connect to Web Serial:', e);
      this.isDirectBrowserMode = false;
    }
    return false;
  }

  /**
   * Continuous read loop from the Web Serial port
   */
  private async startReadingDirectly(): Promise<void> {
    if (this.isReadingDirectly) return;
    this.isReadingDirectly = true;

    try {
      while (this.directPort && this.directPort.readable) {
        const textDecoder = new TextDecoderStream();
        const readableStreamClosed = this.directPort.readable.pipeTo(textDecoder.writable);
        const reader = textDecoder.readable.getReader();
        this.directReader = reader;

        try {
          let buffer = '';
          while (true) {
            const { value, done } = await reader.read();
            if (done) {
              break;
            }
            if (value) {
              buffer += value;
              
              const startStr = this.directStartString;
              const endStr = this.directEndString;

              if (startStr && endStr) {
                let startIndex = buffer.indexOf(startStr);
                let endIndex = buffer.indexOf(endStr, startIndex + startStr.length);

                while (startIndex !== -1 && endIndex !== -1) {
                  const rawVal = buffer.substring(startIndex + startStr.length, endIndex);
                  this.parseAndSetDirectWeight(rawVal);
                  
                  buffer = buffer.substring(endIndex + endStr.length);
                  startIndex = buffer.indexOf(startStr);
                  endIndex = buffer.indexOf(endStr, startIndex + startStr.length);
                }
              } else {
                // Fallback: split by any carriage returns or newlines
                let lines = buffer.split(/[\r\n]+/);
                buffer = lines.pop() || '';

                for (let line of lines) {
                  if (line.trim()) {
                    this.parseAndSetDirectWeight(line);
                  }
                }
              }
            }
          }
        } catch (error) {
          console.error('Error in direct serial reading stream:', error);
        } finally {
          reader.releaseLock();
        }
      }
    } catch (err) {
      console.error('Web Serial read loop failed:', err);
    } finally {
      this.isReadingDirectly = false;
    }
  }

  private parseAndSetDirectWeight(rawString: string) {
    const trimmed = rawString.trim();
    // Match any numeric sequence (e.g. 1507 or 150.7 or 0.681)
    const match = trimmed.match(/[-+]?[0-9]*\.?[0-9]+/);
    if (match) {
      const rawVal = parseFloat(match[0]);
      this.ngZone.run(() => {
        this.directLatestWeight = Math.round((rawVal / this.directDivisor) * 1000) / 1000;
      });
    }
  }

  /**
   * Close direct Web Serial connection
   */
  async closeDirectBrowserSerial(): Promise<void> {
    this.isDirectBrowserMode = false;
    if (this.directReader) {
      try {
        await this.directReader.cancel();
      } catch (e) {}
      this.directReader = null;
    }
    if (this.directPort) {
      try {
        await this.directPort.close();
      } catch (e) {}
      this.directPort = null;
    }
  }

  /**
   * Fetch weight directly from the local Express agent
   */
  getWeightFromAgent(): Observable<any> {
    if (this.isDirectBrowserMode) {
      return of({
        success: true,
        weight: this.directLatestWeight,
        port: 'BROWSER_DIRECT',
        enabled: true,
        simulated: false
      });
    }

    return this.http.get(`${this.localAgentUrl}/weight`).pipe(
      catchError(err => {
        console.warn('Local agent weight fetch failed.');
        return of({ success: false, weight: 0, error: err });
      })
    );
  }

  /**
   * Trigger local agent port auto-detection
   */
  autoDetectLocalPorts(): Observable<any> {
    return this.http.get(`${this.localAgentUrl}/auto-detect`).pipe(
      catchError(err => {
        console.warn('Local agent auto detect ports failed.');
        return of({ success: false, ports: [], error: err });
      })
    );
  }

  /**
   * Set mock weight in local agent (for development/testing)
   */
  setMockWeight(weight: number | null): Observable<any> {
    if (this.isDirectBrowserMode) {
      if (weight !== null) {
        const val = parseFloat(weight.toString());
        this.directLatestWeight = Math.round((val / this.directDivisor) * 1000) / 1000;
      } else {
        this.directLatestWeight = 0;
      }
      return of({ success: true });
    }

    const payload = weight !== null ? { weight } : {};
    return this.http.post(`${this.localAgentUrl}/mock-weight`, payload).pipe(
      catchError(err => {
        return of({ success: false, error: err });
      })
    );
  }

  /**
   * Fetch agent status
   */
  getLocalAgentStatus(): Observable<any> {
    if (this.isDirectBrowserMode) {
      return of({
        initialized: true,
        config: {
          com_port: 'BROWSER_DIRECT',
          enabled: true,
          calibration: this.directDivisor
        },
        latest_weight: this.directLatestWeight,
        mock_weight_active: true,
        mock_weight_value: this.directLatestWeight * this.directDivisor
      });
    }

    return this.http.get(`${this.localAgentUrl}/status`).pipe(
      catchError(err => {
        return of({ success: false, status: 'Offline', error: err });
      })
    );
  }

  isDirectBrowserConnected(): boolean {
    return !!(this.directPort && this.directPort.readable);
  }

  getDirectLatestWeight(): number {
    return this.directLatestWeight;
  }

  getDirectDeviceName(): string {
    if (!this.directPort) return 'Web Serial Device';
    const info = (this.directPort as any).getInfo();
    if (!info) return 'Web Serial Device';

    const vid = info.usbVendorId;
    const pid = info.usbProductId;
    if (!vid) return 'Web Serial Device';

    const vidHex = '0x' + vid.toString(16).padStart(4, '0').toUpperCase();
    const pidHex = pid ? '0x' + pid.toString(16).padStart(4, '0').toUpperCase() : '';

    let vendorName = 'USB Device';
    if (vid === 0x1a86) vendorName = 'CH340 USB-Serial';
    else if (vid === 0x0403) vendorName = 'FTDI USB-Serial';
    else if (vid === 0x10c4) vendorName = 'CP210x USB-Serial';
    else if (vid === 0x067b) vendorName = 'Prolific USB-Serial';
    else if (vid === 0x2341) vendorName = 'Arduino Device';

    if (pidHex) {
      return `${vendorName} (${vidHex}:${pidHex})`;
    }
    return `${vendorName} (${vidHex})`;
  }
}
